/* eslint-disable sort-imports */
import { differenceInMinutes } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  ALL,
  FILTER_ATTENDANCE_STATUS_KEY,
  FILTER_LOCATION_KEY,
  FILTER_MANAGER_KEY, FILTER_PUBLISHED_BY_KEY, FILTER_SHIFT_LOCATION_KEY, FILTER_SHIFT_WORK_CENTERS_KEY,
  FILTER_WORK_CENTERS_KEY,
  FILTER_WORKER_TYPE_KEY,
  HOURS_COMPLETED,
  NO_CALL_NO_SHOWS,
  WORKER_CANCELLATIONS,
  WORKERS_WITH_ATLEAST_1_SHIFT,
  WORKERS_WITH_NO_SHIFTS
} from "../../../assets/constants";
import { formatDate } from "../../../utils/helpers";
import { WORKER_TYPES } from "../../filters/constants";
import { Filter } from "../../filters/filter-accordion/types";
import { DataRelation, FilterContext } from "../../filters/filter-context";
import {
	getMonthlyActivityReport,
	getWorkerActivityFilters,
} from "../store/worker-activity-slice";
import {
	ColumnSelector,
	Metrics,
	Shift,
	Skill,
	Worker,
} from "../store/worker-activity-slice-types";
import { useWorkerActivityData } from "./use-worker-activity-data";

export const useMonthActivityController = () => {
	const {
		start,
		end,
		monthlyActivityReportData,
		workerActivityFilters,
		workerActivityFiltersStatus,
		allMonthlyActivityReportData,
		isWorkerSubTypeEnabled,
	} = useWorkerActivityData();
	const { t } = useTranslation();

	// Define default columns based on whether worker sub type is enabled
	const DEFAULT_COLUMNS: ColumnSelector[] = [
		{ headerName: t("worker-activity_worker-name"), field: "workerName", checked: true },
		{ headerName: t("worker-activity_worker-type"), field: "workerTypeText", checked: false },
		...(isWorkerSubTypeEnabled ? [{ headerName: t("worker-activity_worker-sub-type"), field: "workerSubTypeText", checked: false }] : []),
		{ headerName: t("worker-activity_shift-start-date"), field: "shiftStartDate", checked: true },
		{ headerName: t("worker-activity_shift-hours"), field: "shiftHours", checked: true },
		{ headerName: t("worker-activity_worker-hours"), field: "workerAvailability", checked: true },
		{ headerName: t("worker-activity_shift-location"), field: "shiftLocation", checked: true },
		{ headerName: t("worker-activity_work-center"), field: "workcenter", checked: true },
		{ headerName: t("worker-activity_published-by"), field: "publishedByName", checked: true },
		{ headerName: t("worker-activity_reporting-manager"), field: "reportingManagerName", checked: false },
		{
			headerName: t("worker-activity_job-role-skills-required"),
			field: "jobRoleSkills",
			checked: true,
		},
		{ headerName: t("worker-activity_status"), field: "status", checked: true },
		{ headerName: t("worker-activity_cancellation-status"), field: "cancellationStatus", checked: true },
		{ headerName: t("worker-activity_worker-location"), field: "workerLocation", checked: false },
		{ headerName: t("worker-activity_worker-work-center"), field: "workerWorkcenter", checked: false },
		{
			headerName: t("worker-activity_supervisor-comments"),
			field: "supervisorComments",
			checked: true,
		},
	];

	const [columnSelector, setColumnSelector] =
		useState<ColumnSelector[]>(DEFAULT_COLUMNS);
	const [date, setDate] = useState<[string, string]>([start, end]);
	const [search, setSearch] = useState<string>("");
	const [selectedKPI, setSelectedKPI] = useState(ALL);
	const [sidebarFilteredData, setSidebarFilteredData] = useState<Shift[]>([]);
	const [KPICount, setKPICount] = useState({
		"WORKERS WITH AT LEAST 1 SHIFT": { count: 0, percentage: 0 },
		"WORKERS WITH NO SHIFTS": { count: 0, percentage: 0 },
		"HOURS COMPLETED": { count: 0, percentage: 0 },
		"NO CALL NO SHOWS": { count: 0, percentage: 0 },
		"WORKER CANCELLATIONS": { count: 0, percentage: 0 },
		ALL: { count: 0, percentage: 100 },
	});
	const dispatch = useDispatch();
	const filterContext = useContext(FilterContext);
	if (!filterContext) {
		throw new Error("FilterContext must be used within a FilterProvider");
	}
	const { appliedFilters, open, filters, setFilterOpen, setFilters, setDataRelations, setIsLoading } = filterContext;

	const searchTerm = useMemo(() => search.toLowerCase(), [search]);

	const mapWorkerToShiftData = (item: Worker) => {
		if (item.shifts.length > 0) {
			return item.shifts.map((shift: Shift) => ({
				...shift,
				id: `${item.workerId}-${shift.shiftId}`,
				workerId: item.workerId,
				workerName: `${item.firstName} ${item.lastName}`,
				isShiftAvailable: true,
				workerTypeId: item.workerTypeId,
				workerSubTypeId: item.workerSubTypeId,
				workerTypeText: item.workerTypeText,
				workerSubTypeText: item.workerSubTypeText,
				reportingManagerId: item.reportingManagerId,
				reportingManagerName: item.reportingManagerName,
				workerWorkCenterId: item.workcenterId,
				workerLocationId: item.locationId,
				workerWorkCenterName: item.workerWorkCenterName,
				workerLocationName: item.workerLocationName,
			}));
		}

		return [
			{
				id: `${item.workerId}`,
				workerId: item.workerId,
				workerName: `${item.firstName} ${item.lastName}`,
				isShiftAvailable: false,
				workerTypeId: item.workerTypeId,
				workerSubTypeId: item.workerSubTypeId,
				workerTypeText: item.workerTypeText,
				workerSubTypeText: item.workerSubTypeText,
				reportingManagerId: item.reportingManagerId,
				reportingManagerName: item.reportingManagerName,
				workerWorkCenterId: item.workcenterId,
				workerLocationId: item.locationId,
				workerWorkCenterName: item.workerWorkCenterName,
				workerLocationName: item.workerLocationName,
				...DEFAULT_SHIFT,
			},
		];
	};

	// Get unique worker IDs from shift data
	const getUniqueWorkerIds = (shiftData: any[]) => {
		return [...new Set(shiftData.map((item) => item.workerId))];
	};

	const tableData = useMemo(() => {
		if (allMonthlyActivityReportData?.length > 0) {
			const allMonthlyActivityReportDataWithWorkerType =
				allMonthlyActivityReportData.map((item) => ({
					...item,
					workerTypeText:
						workerActivityFilters?.workerTypes?.find(
							(workerType) => workerType.id === item.workerTypeId
						)?.name || "",
					workerSubTypeText:
						workerActivityFilters?.workerSubTypes?.find(
							(workerSubType) => workerSubType.id === item.workerSubTypeId
						)?.name || "",
					reportingManagerName:
						workerActivityFilters?.managers?.find(
							(manager) => manager.id === item.reportingManagerId
						)?.name || "",
					reportingManagerId: item.reportingManagerId,
					workerWorkCenterName:
						workerActivityFilters?.workCenters?.find(
							(workCenter) => workCenter.id === item.workcenterId
						)?.name || "",
					workerLocationName:
						workerActivityFilters?.locations?.find(
							(location) => location.id === item.locationId
						)?.name || "",
					workerWorkCenterId: item.workcenterId,
					workerLocationId: item.locationId,
				}));
			return allMonthlyActivityReportDataWithWorkerType.flatMap(
				mapWorkerToShiftData
			);
		}
		return [];
	}, [workerActivityFilters, allMonthlyActivityReportData]);

	const getSearchableFields = (
		item: Shift & { workerName: string; isShiftAvailable: boolean; reportingManagerName: string; workerTypeText: string; workerSubTypeText: string; workerLocationName: string; workerWorkCenterName: string }
	) => {
		const baseFields = [
			item.workerName,
			item.shiftLocationName,
			item.shiftWorkcenterName,
			item.publishedByName,
			item.jobName,
			item.skills
				? item.skills.map((skill: Skill) => skill.skillName).join(", ")
				: "",
			statusObj[item.attendanceStatus?.toLowerCase()]?.title || "",
			item.supervisorComments,
			item.reportingManagerName,
			item.workerTypeText,
			item.workerSubTypeText,
			item.workerLocationName,
			item.workerWorkCenterName,
		];

		if (item.isShiftAvailable) {
			const shiftDate = formatDate({
				dateStr: item.shiftStartDateTime,
				formatType: "NUMERIC_DATE",
			});
			const shiftTime = formatDate({
				dateStr: item.shiftStartDateTime,
				formatType: "TIME_RANGE",
				endDateStr: item.shiftEndDateTime,
			});
			return [...baseFields, shiftTime, shiftDate];
		}

		return [...baseFields, "", ""];
	};

	const filteredData = useMemo(() => {
		let filteredResult = tableData;
		if (appliedFilters.length > 0) {
			filteredResult = filteredResult.filter((item) => {
				return appliedFilters.every((filter) => {
					switch (filter.type) {
						case "Shift Location":
							return filter.data.some(
								(location) => location?.name === item?.shiftLocationName
							);
						case "Shift Work Center":
							return filter.data.some(
								(workCenter) => workCenter?.name === item?.shiftWorkcenterName
							);
						case "Published By":
							return filter.data.some(
								(manager) => manager?.name === item?.publishedByName
							);
						case "Status":
							return filter.data.some(
								(status) =>
									status?.name ===
									statusObj[item?.attendanceStatus as keyof typeof statusObj]
										?.title
							);
						case WORKER_TYPES: {
							// Get all the selected IDs
							const selectedIds = filter.data.map((item) => Number(item.id));

							// Check if this item's worker type is selected
							const isParentSelected = selectedIds.includes(
								Number(item.workerTypeId)
							);

							// Check if this item's subtype is selected (with 1000 offset)
							const isSubtypeSelected =
								item.workerSubTypeId &&
								selectedIds.includes(
									Number(item.workerSubTypeId) + 1000
								);

							// Check if any subtypes are selected at all (looking for IDs >= 1000)
							const anySubtypesSelected = selectedIds.some((id) => id >= 1000);


							// Logic:
							// 1. If parent is selected and no subtypes are selected, show all items with that worker type
							// 2. If parent is selected and subtypes are selected, only show if the specific subtype is selected

							if (isParentSelected) {
								// If no subtypes are selected at all, match any item with the parent type
								if (!anySubtypesSelected) {
									return true;
								}

								// If subtypes are selected, must match a selected subtype
								if (isSubtypeSelected) {
									return true;
								}

								return false;
							}
							
							return false;
						}
						case "Reporting Manager":
							return filter.data.some(
								(manager) => Number(manager?.id) === Number(item?.reportingManagerId)
							);
						case "Worker Location":
							return filter.data.some(
								(location) => Number(location?.id) === Number(item?.workerLocationId)
							);
						case "Worker Work Center":
							return filter.data.some(
								(workCenter) => Number(workCenter?.id) === Number(item?.workerWorkCenterId)
							);
						default:
							return true;
					}
				});
			});
		}
		setSidebarFilteredData(filteredResult);
		return filteredResult.filter((item) =>
			getSearchableFields(item)
				.map((field) => (field ?? "").toLowerCase())
				.some((field) => field.includes(searchTerm))
		);
	}, [tableData, searchTerm, appliedFilters]);

	const finalFilteredData = useMemo(() => {
		if (selectedKPI === ALL) {
			return filteredData;
		}

		if (selectedKPI === NO_CALL_NO_SHOWS) {
			return filteredData.filter(
				(item) =>
					item.isShiftAvailable && item.attendanceStatus?.toLowerCase() === "a"
			);
		}

		if (selectedKPI === WORKERS_WITH_NO_SHIFTS) {
			// Get unique worker IDs that have no shifts
			const workerIds = getUniqueWorkerIds(filteredData);
			const workersWithNoShifts = workerIds.filter((workerId) => {
				const workerShifts = filteredData.filter(
					(item) => item.workerId === workerId && item.isShiftAvailable
				);
				return workerShifts.length === 0;
			});

			return filteredData.filter((item) =>
				workersWithNoShifts.includes(item.workerId)
			);
		}

		if (selectedKPI === WORKERS_WITH_ATLEAST_1_SHIFT) {
			// Get unique worker IDs that have at least 1 shift
			const workerIds = getUniqueWorkerIds(filteredData);
			const workersWithShifts = workerIds.filter((workerId) => {
				const workerShifts = filteredData.filter(
					(item) => item.workerId === workerId && item.isShiftAvailable
				);
				return workerShifts.length > 0;
			});

			return filteredData.filter((item) =>
				workersWithShifts.includes(item.workerId)
			);
		}

		if (selectedKPI === HOURS_COMPLETED) {
			return filteredData.filter(
				(item) =>
					item.isShiftAvailable && item.attendanceStatus?.toLowerCase() !== "a"
			);
		}

		if (selectedKPI === WORKER_CANCELLATIONS) {
			return filteredData.filter(
				(item) =>
					item.isShiftAvailable &&
					item.attendanceStatus?.toLowerCase() === "cancelled"
			);
		}

		return filteredData;
	}, [filteredData, selectedKPI]);

	// Calculate KPI counts based on the filtered data
	const calculateKPICounts = useCallback((data: any[]) => {
		// Get unique workers from the filtered data
		const uniqueWorkerIds = getUniqueWorkerIds(data);
		const totalWorkers = uniqueWorkerIds.length;
		const totalShifts = data.filter((item) => item.isShiftAvailable).length;
		const totalHours = data.reduce((acc, item) => {
			if (
				item.isShiftAvailable &&
				item.shiftStartDateTime &&
				item.shiftEndDateTime
			) {
				const hours = Math.round(
					Math.abs(
						differenceInMinutes(
							new Date(item.shiftEndDateTime),
							new Date(item.shiftStartDateTime)
						)
					) / 60
				);
				acc += hours;
			}
			return acc;
		}, 0);

		// Group shifts by worker ID
		const shiftsByWorker = data.reduce((acc, item) => {
			if (!acc[item.workerId]) {
				acc[item.workerId] = [];
			}
			if (item.isShiftAvailable) {
				acc[item.workerId].push(item);
			}
			return acc;
		}, {});

		// Calculate metrics
		let workersWithShifts = 0;
		let hoursCompleted = 0;
		let noCallNoShows = 0;
		let workerCancelled = 0;

		Object.keys(shiftsByWorker).forEach((workerId) => {
			const workerShifts = shiftsByWorker[workerId];

			if (workerShifts.length > 0) {
				workersWithShifts++;

				workerShifts.forEach((shift: any) => {
					if (shift.shiftStatus?.toLowerCase() === "c") {
						workerCancelled++;
					} else if (shift.attendanceStatus?.toLowerCase() === "a") {
						noCallNoShows++;
					} else if (shift.shiftStartDateTime && shift.shiftEndDateTime) {
						const hours = Math.round(
							Math.abs(
								differenceInMinutes(
									new Date(shift.shiftEndDateTime),
									new Date(shift.shiftStartDateTime)
								)
							) / 60
						);
						hoursCompleted += hours;
					}
				});
			}
		});

		const workersWithNoShifts = totalWorkers - workersWithShifts;

		return {
			"WORKERS WITH AT LEAST 1 SHIFT": {
				count: workersWithShifts,
				percentage:
					totalWorkers > 0
						? Math.round((workersWithShifts / totalWorkers) * 100)
						: 0,
			},
			"WORKERS WITH NO SHIFTS": {
				count: workersWithNoShifts,
				percentage:
					totalWorkers > 0
						? Math.round((workersWithNoShifts / totalWorkers) * 100)
						: 0,
			},
			"HOURS COMPLETED": {
				count: hoursCompleted,
				percentage:
					hoursCompleted > 0
						? Math.round((hoursCompleted / totalHours) * 100)
						: 0,
			},
			"NO CALL NO SHOWS": {
				count: noCallNoShows,
				percentage:
					noCallNoShows > 0
						? Math.round((noCallNoShows / totalShifts) * 100)
						: 0,
			},
			"WORKER CANCELLATIONS": {
				count: workerCancelled,
				percentage:
					workerCancelled > 0
						? Math.round((workerCancelled / totalShifts) * 100)
						: 0,
			},
			ALL: {
				count: totalWorkers,
				percentage: 100,
			},
		};
	}, []);

	useEffect(() => {
		if (date[0] && date[1]) {
			setSelectedKPI(ALL);
			dispatch(
				getMonthlyActivityReport({ startDate: date[0], endDate: date[1] })
			);
		}
	}, [date, dispatch]);

	useEffect(() => {
		dispatch(getWorkerActivityFilters());
	}, [dispatch]);

	// Update KPI count whenever filtered data changes
	useEffect(() => {
		const newKPICount = calculateKPICounts(sidebarFilteredData);
		setKPICount(newKPICount);
	}, [sidebarFilteredData, calculateKPICounts]);

	useEffect(() => {
		if (workerActivityFiltersStatus === "pending") {
			setIsLoading?.(true);
		} else if (workerActivityFiltersStatus === "fulfilled") {
			setIsLoading?.(false);
			const newFilters: Filter[] = [];

			// Add Worker Type filter
			if (workerActivityFilters?.workerTypes?.length > 0) {
				// Create a completely new array of objects to avoid type issues
				const workerTypeData: Array<{
					id: number;
					name: string;
					parentId?: number;
					originalType?: any;
				}> = [];

				// Add main worker types first
				workerActivityFilters.workerTypes.forEach((type) => {
					workerTypeData.push({
						id: parseInt(type.id.toString()), // Convert to number and back to string to normalize format
						name: type.name,
						originalType: type.type, // Store original type info
					});
				});

				// Then add subtypes with consistent ID offsetting
				if (workerActivityFilters?.workerSubTypes?.length > 0 && isWorkerSubTypeEnabled) {
					workerActivityFilters.workerSubTypes.forEach((subType) => {
						workerTypeData.push({
							id: parseInt(subType.id.toString()) + 1000, // Add offset consistently with filter logic
							name: subType.name,
							parentId: parseInt(subType.workerTypeId.toString()), // Reference to the parent
						});
					});
				}

				newFilters.push({
					id: 0,
					name: WORKER_TYPES,
          key: FILTER_WORKER_TYPE_KEY,
					data: workerTypeData,
					reducedData: workerTypeData,
					searchedData: workerTypeData,
				});
			}

			// Add Worker location and worker workcenter filters

			if(workerActivityFilters?.locations?.length > 0) {
				newFilters.push({
					id: 1,
					name: "Worker Location",
          key: FILTER_LOCATION_KEY,
					data: workerActivityFilters.locations,
					reducedData: workerActivityFilters.locations,
					searchedData: workerActivityFilters.locations,
				});
			}

			if (workerActivityFilters?.workCenters?.length > 0) {
				newFilters.push({
					id: 2,
					name: "Worker Work Center",
          key: FILTER_WORK_CENTERS_KEY,
					data: workerActivityFilters.workCenters,
					reducedData: workerActivityFilters.workCenters,
					searchedData: workerActivityFilters.workCenters,
				});
			}

			if (workerActivityFilters?.attendanceStatuses?.length > 0) {
				newFilters.push({
					id: 3,
					name: "Status",
          key: FILTER_ATTENDANCE_STATUS_KEY,
					data: workerActivityFilters.attendanceStatuses,
					reducedData: workerActivityFilters.attendanceStatuses,
					searchedData: workerActivityFilters.attendanceStatuses,
				});
			}

			if (workerActivityFilters?.locations?.length > 0) {
				newFilters.push({
					id: 4,
					name: "Shift Location",
          key: FILTER_SHIFT_LOCATION_KEY,
					data: workerActivityFilters.locations,
					reducedData: workerActivityFilters.locations,
					searchedData: workerActivityFilters.locations,
				});
			}

			if (workerActivityFilters?.workCenters?.length > 0) {
				newFilters.push({
					id: 5,
					name: "Shift Work Center",
          key: FILTER_SHIFT_WORK_CENTERS_KEY,
					data: workerActivityFilters.workCenters,
					reducedData: workerActivityFilters.workCenters,
					searchedData: workerActivityFilters.workCenters,
				});
			}

			if (workerActivityFilters?.managers?.length > 0) {
				newFilters.push({
					id: 6,
					name: "Published By",
          key: FILTER_PUBLISHED_BY_KEY,
					data: workerActivityFilters.managers,
					reducedData: workerActivityFilters.managers,
					searchedData: workerActivityFilters.managers,
				});
			}
			// Add Reporting Manager filter
			if (workerActivityFilters?.managers?.length > 0) {
				newFilters.push({
					id: 7,
					name: "Reporting Manager",
          key: FILTER_MANAGER_KEY,
					data: workerActivityFilters.managers,
					reducedData: workerActivityFilters.managers,
					searchedData: workerActivityFilters.managers,
				});
			}
			setFilters(newFilters);
      // Clone DataRelations and extend for shift equivalents
      let relations: DataRelation[] = workerActivityFilters?.filterRelationships ?? [];

      // Find relation from locations -> workCenters
      const locationToWorkCenter = relations.find(
        r => r.from === FILTER_LOCATION_KEY && r.to === FILTER_WORK_CENTERS_KEY
      );

      if (locationToWorkCenter) {
        // Create a cloned version for shift filters
        const shiftRelation: DataRelation = {
          from: FILTER_SHIFT_LOCATION_KEY,
          to: FILTER_SHIFT_WORK_CENTERS_KEY,
          mappings: locationToWorkCenter.mappings,
        };
        relations = [...relations, shiftRelation];
      }

      const workCenterToManager = relations.find(
        r => r.from === FILTER_WORK_CENTERS_KEY && r.to === FILTER_MANAGER_KEY
      );

      if (workCenterToManager) {
        const publishedByRelation: DataRelation = {
          from: FILTER_WORK_CENTERS_KEY,
          to: FILTER_PUBLISHED_BY_KEY,
          mappings: workCenterToManager.mappings,
        }
        relations = [...relations, publishedByRelation];
      }
      setDataRelations(relations);
		} else if (workerActivityFiltersStatus === "error") {
			setIsLoading?.(false);
		}
	}, [workerActivityFilters, workerActivityFiltersStatus, isWorkerSubTypeEnabled, setFilters, setDataRelations, setIsLoading]);

	const exportData = () => {
		const data = finalFilteredData.map((item) => {
			const baseExport = {
				"Worker Name": item.workerName,
				"Worker Type": item.workerTypeText || "-",
				...(isWorkerSubTypeEnabled ? { "Worker Sub Type": item.workerSubTypeText || "-" } : {}),
				"Shift Date": "-",
				"Shift Time": "-",
				"Worker Hours": "-",
				"Shift Location": "-",
				"Work Center": "-",
				"Published By": "-",
				"Job Role / Skills Required": "-",
				Status: "-",
				"Cancellation Status": "-",
				"Cancellation Violation Reason": "-",
				"Reporting Manager": item.reportingManagerName || "-",
				"Worker Location": item.workerLocationName || "-",
				"Worker Work Center": item.workerWorkCenterName || "-",
				"Supervisor Comments": "-",
			};

			if (!item.isShiftAvailable) return baseExport;

			const shiftTime = formatDate({
				dateStr: item.shiftStartDateTime,
				formatType: "TIME_RANGE",
				endDateStr: item.shiftEndDateTime,
			});

			const workerAvailability = formatDate({
				dateStr: item.workerShiftStartDateTime,
				formatType: "TIME_RANGE",
				endDateStr: item.workerShiftEndDateTime,
			});

			return {
				...baseExport,
				"Shift Date": formatDate({
					dateStr: item.shiftStartDateTime,
					formatType: "NUMERIC_DATE",
				}),
				"Shift Time": shiftTime,
				"Worker Hours": workerAvailability,
				"Shift Location": item.shiftLocationName,
				"Work Center": item.shiftWorkcenterName,
				"Published By": item.publishedByName,
				"Job Role / Skills Required":
					item.jobName ||
					item.skills?.map((skill) => skill.skillName).join(", ") ||
					"-",
				Status: statusObj[item.attendanceStatus?.toLowerCase()]?.title || "-",
				"Cancellation Status": item.isCancelOK === null || item.isCancelOK === undefined ? "-" : item.isCancelOK ? "In Time" : "Late",
				"Cancellation Violation Reason": item.cancelViolationReason || "-",
				"Supervisor Comments": item.supervisorComments || "-",
			};
		});
		// sort by worker name
		data.sort((a, b) => a["Worker Name"].localeCompare(b["Worker Name"]));
		return data;
	};

	return [
		{
			date,
			exportData,
			filteredData: finalFilteredData,
			sidebarFilteredData,
			search,
			columnSelector,
			open,
			filters,
			selectedKPI,
			KPICount,
		},
		{
			setDate,
			setSearch,
			setColumnSelector,
			setFilterOpen,
			setFilters,
			setSelectedKPI,
		},
	] as const;
};

const DEFAULT_SHIFT: Shift = {
	shiftId: "",
	shiftStartDateTime: "",
	shiftEndDateTime: "",
	shiftLocationName: "",
	shiftWorkcenterName: "",
	publishedByName: "",
	jobName: "",
	skills: [],
	attendanceStatus: "",
	supervisorComments: "",
	shiftStatus: "",
	workerShiftStartDateTime: "",
	workerShiftEndDateTime: "",
	isCancelOK: null,
	cancelViolationReason: null,
	// workerTypeText: "",
	// workerSubTypeText: "",
	// workerTypeId: null,
	// workerSubTypeId: null,
};

interface IStatusObjType {
	[key: string]: {
		title: string;
	};
}

const statusObj: IStatusObjType = {
	p: {
		title: "Present",
	},
	a: {
		title: "No Show",
	},
	t: {
		title: "Tardy",
	},
	e: {
		title: "Excused Absence",
	},
	x: {
		title: "Excused Tardy",
	},
	cancelled: {
		title: "Cancelled",
	},
};
